import type {
    Provider,
    Scope,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import { Text, Tooltip } from "@amzn/storm-ui";
import { isEmpty } from "lodash";
import type { ReactElement } from "react";
import { useCallback } from "react";
import { useStore } from "zustand";

import { scopeToTooltipMap } from "../../../constants/filters";
import { TELEMETRY_ID } from "../../../constants/telemetry";
import useEncodedFilterStateUrl from "../../../hooks/misc/useEncodedFilterStateUrl";
import { FeatureFlagsStore } from "../../../store/featureFlags";
import { discoveryFilterStore } from "../../../store/filters";
import type { IHubContentProvider } from "../../../types/providers";
import { isAccountAgnosticId } from "../../../utils/permission.utils";
import {
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../../utils/telemetry.utils";
import { TagComponent } from "../../common/Tag";
import { StyledFilterMenuItemButton } from "../commonStyles";

interface TypeFilterOptionProps {
    provider: Provider | undefined;
    scope: Scope;
    onClick: () => void;
}

export const TypeFilterOption = (
    props: TypeFilterOptionProps,
): ReactElement => {
    const { provider, scope, onClick } = props;

    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const [contentType, setTypes, resetFilters] = useStore(
        discoveryFilterStore,
        (state) => [state.contentType, state.setTypes, state.resetFilters],
    );
    const [setEncodedFilterStateUrl] = useEncodedFilterStateUrl();

    const getDisabledTooltipMessage = () => {
        if (isAccountAgnosticId()) {
            return "This insight type requires an account selection";
        }
        return "No insights available for this type";
    };

    const onMenuItemClick = useCallback(() => {
        const filters = useUnifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();

        logWithTakt(TELEMETRY_ID.TYPE_OPTION_SELECTION, {
            ...filters,
        });
        if (scope.value !== undefined && provider?.value !== undefined) {
            setTypes(contentType, {
                provider: provider.value as IHubContentProvider,
                scope: scope.value,
            });
        }

        setEncodedFilterStateUrl();

        onClick();
    }, [contentType]);

    const menuItem = (
        <StyledFilterMenuItemButton
            key={scope.label}
            data-testid={`${scope.label}-option`}
            onClick={scope.disabled ? () => {} : onMenuItemClick}
            aria-haspopup="true"
            isDisabled={scope.disabled ?? false}
        >
            <Text>{scope.label}</Text>
            {!isEmpty(scope.tag) && (
                <TagComponent {...scope.tag} inline={true} />
            )}
        </StyledFilterMenuItemButton>
    );

    return (
        <>
            {scope.disabled ? (
                <Tooltip
                    withCloseButton={false}
                    position="right"
                    trigger={menuItem}
                >
                    <Text>{getDisabledTooltipMessage()}</Text>
                </Tooltip>
            ) : scope.value && scopeToTooltipMap[scope.value] ? (
                <Tooltip
                    withCloseButton={false}
                    position="right"
                    trigger={menuItem}
                    transitionDuration={{ enter: 0, exit: 0 }}
                >
                    {scope.value && (
                        <Text>{scopeToTooltipMap[scope.value]}</Text>
                    )}
                </Tooltip>
            ) : (
                menuItem
            )}
        </>
    );
};
